import { useEffect, useId, useState, type KeyboardEvent } from 'react';
import { Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Dialog, Spinner } from '@/components/ui';
import { useConversations, type Conversation } from '@/lib/api';
import { cn, useDebouncedValue } from '@/lib/utils';
import { relativeDay } from './conversation-dates';
import { OrigemTag } from './OrigemTag';
import { SurfaceIcon } from './SurfaceIcon';
import { conversationPath } from './surfaces';
import './ConversationSearchDialog.css';

/** Sem busca, a lista é a das últimas conversas — o "Recentes" do padrão. */
export const RECENT_LIMIT = 15;
/** Espera de digitação antes de perguntar à API. */
export const SEARCH_DEBOUNCE_MS = 250;

export interface ConversationSearchDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * "Procurar conversas": um campo com foco inicial e a lista logo abaixo
 * (recentes sem busca, resultados com ela). Enter ou clique abre a conversa no
 * chat do Conhecimento. O corpo só monta com o diálogo aberto, então a busca
 * nunca roda escondida.
 */
export function ConversationSearchDialog({ open, onClose }: ConversationSearchDialogProps): JSX.Element {
  return (
    <Dialog open={open} onClose={onClose} title="Procurar conversas" size="md">
      <SearchBody onClose={onClose} />
    </Dialog>
  );
}

function SearchBody({ onClose }: { onClose: () => void }): JSX.Element {
  const navigate = useNavigate();
  const listId = useId();
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const q = useDebouncedValue(query.trim(), SEARCH_DEBOUNCE_MS);
  const { data, isPending, isError } = useConversations(q ? { q } : { limit: RECENT_LIMIT });
  const items = data?.items ?? [];

  useEffect(() => setActiveIndex(0), [q]);

  const optionId = (index: number): string => `${listId}-opcao-${index}`;

  const open = (conversation: Conversation): void => {
    onClose();
    navigate(conversationPath(conversation.id));
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, Math.max(items.length - 1, 0)));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const conversation = items[activeIndex];
      if (conversation) open(conversation);
    }
  };

  return (
    <div className="conversation-search">
      <div className="conversation-search__field">
        <Search className="conversation-search__icon" aria-hidden="true" />
        <input
          // O diálogo existe para buscar: o foco nasce no campo, e a trava de
          // foco do Dialog o respeita em vez de mandá-lo para o "Fechar".
          autoFocus
          className="conversation-search__input"
          type="search"
          role="combobox"
          aria-label="Buscar nas conversas"
          aria-expanded="true"
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={items.length > 0 ? optionId(activeIndex) : undefined}
          placeholder="Buscar nas conversas"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={onKeyDown}
        />
        {isPending && <Spinner size={14} label="Buscando conversas" />}
      </div>

      <p className="conversation-search__heading" id={`${listId}-titulo`}>
        {q ? 'Resultados' : 'Recentes'}
      </p>
      <div id={listId} className="conversation-search__list" role="listbox" aria-labelledby={`${listId}-titulo`}>
        {items.map((conversation, index) => (
          <SearchOption
            key={conversation.id}
            id={optionId(index)}
            conversation={conversation}
            active={index === activeIndex}
            onSelect={() => open(conversation)}
            onHover={() => setActiveIndex(index)}
          />
        ))}
      </div>
      {!isPending && items.length === 0 && (
        <p className="conversation-search__empty" role="status">
          {emptyMessage(isError, q)}
        </p>
      )}
    </div>
  );
}

function emptyMessage(failed: boolean, query: string): string {
  if (failed) return 'Não foi possível carregar as conversas.';
  return query ? 'Nenhuma conversa encontrada.' : 'Sem conversas ainda';
}

interface SearchOptionProps {
  id: string;
  conversation: Conversation;
  active: boolean;
  onSelect: () => void;
  onHover: () => void;
}

function SearchOption({ id, conversation, active, onSelect, onHover }: SearchOptionProps): JSX.Element {
  return (
    <button
      id={id}
      type="button"
      role="option"
      // O foco fica no campo; as setas movem a opção ativa (combobox).
      tabIndex={-1}
      aria-selected={active}
      className={cn('conversation-search__option', active && 'conversation-search__option--active')}
      onClick={onSelect}
      onMouseMove={onHover}
    >
      <SurfaceIcon surface={conversation.surface} className="conversation-search__option-icon" />
      <span className="conversation-search__option-title">{conversation.title}</span>
      <OrigemTag surface={conversation.surface} />
      <time className="conversation-search__option-date" dateTime={conversation.lastMessageAt}>
        {relativeDay(conversation.lastMessageAt)}
      </time>
    </button>
  );
}
